/**
 * The authoritative snapshot: a `GameState` on the wire, and back again.
 *
 * `docs/DESIGN.md`, "Netcode contract": "A hash mismatch resyncs the client
 * from a server snapshot." This is that snapshot. It travels one way only —
 * server to client — because it is the *repair*, not the report: a client that
 * has been caught diverging is handed the state it should have had.
 *
 * Two properties matter and are tested directly:
 *
 * 1. **Round-tripping preserves the hash.** `hashState(decode(encode(s)))`
 *    equals `hashState(s)` for any state. If it did not, a resync would leave
 *    the client diverged in a new way and the next checkpoint would fail again.
 * 2. **It is compact.** The board is one string per row, one character per
 *    cell, rather than an array of nulls and letters — a 22-row well is 22
 *    ten-character strings instead of 220 JSON tokens. The transport's default
 *    4KB frame limit is not something a repair message should be flirting with.
 * 3. **Decoding checks everything it reads.** The same decoder restores a
 *    match from a checkpoint file on the server's disk, so it does not assume
 *    its input was written by the encoder: every field is checked for the
 *    type and range it is read as, and a snapshot that fails is refused with
 *    an error that names the field.
 */
import { createGame } from '@interlock/engine';
import type { ActivePiece, Cell, GameState, PieceKind, RotationState } from '@interlock/engine';

/**
 * Bumped when the encoding changes shape. A decoder refuses a version it does
 * not know. `2` is integer gravity: a piece carries `gravityUnits` and the
 * curve carries `speedPermille` (`docs/decisions/0050`).
 */
export const SNAPSHOT_VERSION = 2;

const EMPTY_CELL = '.';
const GARBAGE_CELL = 'G';
const PIECE_CHARS = 'IJLOSTZ';

export interface SnapshotPiece {
  readonly kind: PieceKind;
  readonly rotation: number;
  readonly x: number;
  readonly y: number;
  readonly lockTicks: number;
  readonly lockResets: number;
  readonly gravityUnits: number;
  readonly lastActionWasRotation: boolean;
}

export interface SnapshotGarbage {
  readonly id: number;
  readonly lines: number;
  readonly readyAtTick: number;
}

export interface StateSnapshot {
  readonly v: number;
  readonly seed: number;
  readonly width: number;
  readonly visibleRows: number;
  readonly hiddenRows: number;
  readonly groupGravity: boolean;
  readonly lockDelayMs: number;
  readonly maxLockResets: number;
  readonly nextPreview: number;
  readonly gravity: {
    readonly baseMs: number;
    readonly perLevelMs: number;
    readonly minMs: number;
    readonly linesPerLevel: number;
    readonly speedPermille: number;
  };
  /** One string per row, top row first, one character per cell. */
  readonly board: readonly string[];
  readonly rng: number;
  /** Upcoming pieces as their letters, front first. */
  readonly queue: string;
  readonly pieces: readonly (SnapshotPiece | null)[];
  readonly holds: readonly (PieceKind | null)[];
  readonly holdAvailable: readonly boolean[];
  readonly tick: number;
  readonly lines: number;
  readonly attack: number;
  readonly combo: number;
  readonly backToBack: boolean;
  readonly pendingGarbage: readonly SnapshotGarbage[];
  readonly garbageColumn: number;
  readonly piecesPlaced: number;
  readonly bestChain: number;
  readonly topOut: boolean;
  readonly nextGarbageId: number;
}

function cellChar(cell: Cell): string {
  if (cell === null) return EMPTY_CELL;
  return cell;
}

function charCell(character: string): Cell {
  if (character === EMPTY_CELL) return null;
  if (character === GARBAGE_CELL) return 'G';
  if (PIECE_CHARS.includes(character)) return character as PieceKind;
  throw new Error(`snapshot: unknown board character ${JSON.stringify(character)}`);
}

function isPieceKind(value: unknown): value is PieceKind {
  return typeof value === 'string' && value.length === 1 && PIECE_CHARS.includes(value);
}

export function encodeSnapshot(state: GameState): StateSnapshot {
  const config = state.config;
  return {
    v: SNAPSHOT_VERSION,
    seed: config.seed,
    width: config.width,
    visibleRows: config.visibleRows,
    hiddenRows: config.hiddenRows,
    groupGravity: config.groupGravity,
    lockDelayMs: config.lockDelayMs,
    maxLockResets: config.maxLockResets,
    nextPreview: config.nextPreview,
    gravity: {
      baseMs: config.gravity.baseMs,
      perLevelMs: config.gravity.perLevelMs,
      minMs: config.gravity.minMs,
      linesPerLevel: config.gravity.linesPerLevel,
      speedPermille: config.gravity.speedPermille,
    },
    board: state.board.rows.map((row) => row.map(cellChar).join('')),
    rng: state.rng.seed,
    queue: state.queue.join(''),
    pieces: state.pieces.map((piece) =>
      piece === null
        ? null
        : {
            kind: piece.kind,
            rotation: piece.rotation,
            x: piece.x,
            y: piece.y,
            lockTicks: piece.lockTicks,
            lockResets: piece.lockResets,
            gravityUnits: piece.gravityUnits,
            lastActionWasRotation: piece.lastActionWasRotation,
          },
    ),
    holds: [...state.holds],
    holdAvailable: [...state.holdAvailable],
    tick: state.tick,
    lines: state.lines,
    attack: state.attack,
    combo: state.combo,
    backToBack: state.backToBack,
    pendingGarbage: state.pendingGarbage.map((entry) => ({
      id: entry.id,
      lines: entry.lines,
      readyAtTick: entry.readyAtTick,
    })),
    garbageColumn: state.garbageColumn,
    piecesPlaced: state.piecesPlaced,
    bestChain: state.bestChain,
    topOut: state.topOut,
    nextGarbageId: state.nextGarbageId,
  };
}

/** Thrown by `decodeSnapshot`, with the path of the field that was wrong. */
function refuse(path: string, expected: string, value: unknown): never {
  throw new Error(
    `snapshot: ${path} must be ${expected}, got ${JSON.stringify(value) ?? String(value)}`,
  );
}

function wholeNumber(
  value: unknown,
  path: string,
  min: number,
  max = Number.MAX_SAFE_INTEGER,
): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < min || value > max) {
    return refuse(
      path,
      max === Number.MAX_SAFE_INTEGER
        ? `a whole number of at least ${String(min)}`
        : `a whole number from ${String(min)} to ${String(max)}`,
      value,
    );
  }
  return value;
}

function flag(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') return refuse(path, 'true or false', value);
  return value;
}

function list(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) return refuse(path, 'a list', value);
  return value as readonly unknown[];
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return refuse(path, 'an object', value);
  }
  return value as Record<string, unknown>;
}

function text(value: unknown, path: string): string {
  if (typeof value !== 'string') return refuse(path, 'text', value);
  return value;
}

function decodePiece(value: unknown, path: string): ActivePiece | null {
  if (value === null) return null;
  const piece = record(value, path);
  if (!isPieceKind(piece['kind'])) return refuse(`${path}.kind`, 'a piece letter', piece['kind']);
  return {
    kind: piece['kind'],
    // The engine's `RotationState` is 0..3. Anything else is a corrupt
    // snapshot, and it is refused: masking it into range would hand the game a
    // piece in a rotation nobody put it in.
    rotation: wholeNumber(piece['rotation'], `${path}.rotation`, 0, 3) as RotationState,
    x: wholeNumber(piece['x'], `${path}.x`, Number.MIN_SAFE_INTEGER),
    y: wholeNumber(piece['y'], `${path}.y`, Number.MIN_SAFE_INTEGER),
    lockTicks: wholeNumber(piece['lockTicks'], `${path}.lockTicks`, 0),
    lockResets: wholeNumber(piece['lockResets'], `${path}.lockResets`, 0),
    // Negative for a tick at a time under an eased gravity modifier; never a
    // fraction.
    gravityUnits: wholeNumber(
      piece['gravityUnits'],
      `${path}.gravityUnits`,
      Number.MIN_SAFE_INTEGER,
    ),
    lastActionWasRotation: flag(piece['lastActionWasRotation'], `${path}.lastActionWasRotation`),
  };
}

/**
 * Rebuild a `GameState`, or throw an error naming the first field that is
 * wrong.
 *
 * `createGame` is used to produce a correctly shaped, frozen `EngineConfig` and
 * a board of the right dimensions, and every mutable field is then overwritten
 * from the snapshot. Constructing the config by hand would risk drifting from
 * `createConfig`'s defaults, and the config is inside the hash.
 */
export function decodeSnapshot(snapshot: StateSnapshot): GameState {
  const raw = record(snapshot as unknown, 'the snapshot');
  if (raw['v'] !== SNAPSHOT_VERSION) {
    throw new Error(
      `snapshot: version ${String(raw['v'])} cannot be read by version ${String(SNAPSHOT_VERSION)}`,
    );
  }

  const gravity = record(raw['gravity'], 'gravity');
  const width = wholeNumber(raw['width'], 'width', 1);
  const state = createGame({
    seed: wholeNumber(raw['seed'], 'seed', Number.MIN_SAFE_INTEGER),
    width,
    visibleRows: wholeNumber(raw['visibleRows'], 'visibleRows', 1),
    hiddenRows: wholeNumber(raw['hiddenRows'], 'hiddenRows', 0),
    groupGravity: flag(raw['groupGravity'], 'groupGravity'),
    lockDelayMs: wholeNumber(raw['lockDelayMs'], 'lockDelayMs', 0),
    maxLockResets: wholeNumber(raw['maxLockResets'], 'maxLockResets', 0),
    nextPreview: wholeNumber(raw['nextPreview'], 'nextPreview', 0),
    gravity: {
      baseMs: wholeNumber(gravity['baseMs'], 'gravity.baseMs', 0),
      perLevelMs: wholeNumber(gravity['perLevelMs'], 'gravity.perLevelMs', 0),
      minMs: wholeNumber(gravity['minMs'], 'gravity.minMs', 0),
      linesPerLevel: wholeNumber(gravity['linesPerLevel'], 'gravity.linesPerLevel', 0),
      speedPermille: wholeNumber(gravity['speedPermille'], 'gravity.speedPermille', 0),
    },
  });

  const board = list(raw['board'], 'board');
  if (board.length !== state.board.height) {
    throw new Error(
      `snapshot: board has ${String(board.length)} rows, expected ${String(state.board.height)}`,
    );
  }
  state.board.rows = board.map((value, index) => {
    const row = text(value, `board[${String(index)}]`);
    if (row.length !== width) {
      throw new Error(`snapshot: row has ${String(row.length)} cells, expected ${String(width)}`);
    }
    return [...row].map(charCell);
  });

  state.rng.seed = wholeNumber(raw['rng'], 'rng', 0, 0xffff_ffff);
  state.queue = [...text(raw['queue'], 'queue')].map((character) => {
    if (!isPieceKind(character)) {
      throw new Error(`snapshot: unknown queue character ${JSON.stringify(character)}`);
    }
    return character;
  });

  const pieces = list(raw['pieces'], 'pieces');
  if (pieces.length < 1) refuse('pieces', 'at least one slot', pieces);
  state.pieces = pieces.map((piece, slot) => decodePiece(piece, `pieces[${String(slot)}]`));

  const holds = list(raw['holds'], 'holds');
  if (holds.length !== pieces.length) refuse('holds', 'one entry per slot', holds);
  state.holds = holds.map((held, slot) =>
    held === null || isPieceKind(held)
      ? held
      : refuse(`holds[${String(slot)}]`, 'a piece letter or null', held),
  );

  const holdAvailable = list(raw['holdAvailable'], 'holdAvailable');
  if (holdAvailable.length !== pieces.length) {
    refuse('holdAvailable', 'one entry per slot', holdAvailable);
  }
  state.holdAvailable = holdAvailable.map((available, slot) =>
    flag(available, `holdAvailable[${String(slot)}]`),
  );

  state.tick = wholeNumber(raw['tick'], 'tick', 0);
  state.lines = wholeNumber(raw['lines'], 'lines', 0);
  state.attack = wholeNumber(raw['attack'], 'attack', 0);
  state.combo = wholeNumber(raw['combo'], 'combo', -1);
  state.backToBack = flag(raw['backToBack'], 'backToBack');
  state.pendingGarbage = list(raw['pendingGarbage'], 'pendingGarbage').map((value, index) => {
    const path = `pendingGarbage[${String(index)}]`;
    const entry = record(value, path);
    return {
      id: wholeNumber(entry['id'], `${path}.id`, 0),
      lines: wholeNumber(entry['lines'], `${path}.lines`, 1),
      readyAtTick: wholeNumber(entry['readyAtTick'], `${path}.readyAtTick`, 0),
    };
  });
  state.garbageColumn = wholeNumber(raw['garbageColumn'], 'garbageColumn', 0, width - 1);
  state.piecesPlaced = wholeNumber(raw['piecesPlaced'], 'piecesPlaced', 0);
  state.bestChain = wholeNumber(raw['bestChain'], 'bestChain', 0);
  state.topOut = flag(raw['topOut'], 'topOut');
  state.nextGarbageId = wholeNumber(raw['nextGarbageId'], 'nextGarbageId', 1);

  return state;
}
